import { useCallback, useEffect, useRef, useState } from 'react';
import { Map as GLMap, useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { SOURCE_ID, SOURCE_LAYER } from './LightningLayer.constants';
import { StrikeCounts, StrikeTimes, TimeWindow } from './LightningLayer.types';
import {
  collectStrikeTimes,
  countStrikesInWindow,
  isLightningSourceLoadedEvent,
} from './LightningLayer.utils';

type UseLightningStrikeCountsResult = {
  counts: StrikeCounts | null;
  isLoading: boolean;
};

/**
 * Strike counts in view for `timeWindow`. Strikes are collected only when the
 * data can change, since every frame's filter change reloads the source.
 */
export const useLightningStrikeCounts = (
  timeWindow: TimeWindow | null,
): UseLightningStrikeCountsResult => {
  const mapInstance = useMapInstance();
  const [strikeTimes, setStrikeTimes] = useState<StrikeTimes | null>(null);
  // The view changed since the last collection
  const [isStale, setIsStale] = useState(false);
  // Collect on the next load of the source
  const isPendingRef = useRef(true);

  // Returns false while the source is still loading
  const collectIfLoaded = useCallback((map: GLMap): boolean => {
    const bounds = map.getBounds();
    if (!bounds || !map.isSourceLoaded(SOURCE_ID)) return false;
    const features = map.querySourceFeatures(SOURCE_ID, {
      sourceLayer: SOURCE_LAYER,
    });
    setStrikeTimes(collectStrikeTimes(features, bounds));
    setIsStale(false);
    return true;
  }, []);

  // Remounts on an already loaded source get no load event
  useEffect(() => {
    const map = mapInstance?.getMap();
    if (map) collectIfLoaded(map);
  }, [mapInstance, collectIfLoaded]);

  useMapEvent('movestart', () => setIsStale(true), mapInstance, true);

  useMapEvent(
    'moveend',
    (event) => {
      // New tiles may not be requested yet, so also collect on the next load
      isPendingRef.current = true;
      collectIfLoaded(event.target);
    },
    mapInstance,
    true,
  );

  useMapEvent(
    'sourcedata',
    (event) => {
      if (event.sourceId !== SOURCE_ID) return;
      if (event.sourceDataType === 'metadata') {
        isPendingRef.current = true;
        return;
      }
      if (!isPendingRef.current || event.target.isMoving()) return;
      if (!isLightningSourceLoadedEvent(event)) return;
      if (collectIfLoaded(event.target)) isPendingRef.current = false;
    },
    mapInstance,
    true,
  );

  const counts =
    strikeTimes && timeWindow
      ? countStrikesInWindow(strikeTimes, timeWindow)
      : null;

  return { counts, isLoading: counts === null || isStale };
};
